import dayjs, { Dayjs } from "dayjs";
import { schedulingV2 } from "lobbie";
import { memo, useCallback, useEffect, useMemo } from "react";
import Button from "react-bootstrap/Button";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import CalendarWeek from "src/components/shared/calendar/CalendarWeek";
import LobbieDatePickerV2 from "src/components/shared/elements/datetime/LobbieDatePickerV2";
import CenteredLoadingSpinner from "src/components/shared/loading/CenteredLoadingSpinner";
import { DATE_TIME_FORMATS } from "src/constants";
import { useCalendar, useCalendarMonthYear } from "src/hooks/calendar";
import { usePatientSelfSchedulingContext } from "src/hooks/patient_scheduling/usePatientSelfSchedulingContext";
import { useThrottleFunction } from "src/hooks/useThrottle";
import { logDev, zeroTime } from "src/utils";
import { LOBBIE_COLORS } from "src/utils/colors";

interface IWeek {
    week: number;
    days: (Dayjs | null)[];
}

interface IProps {
    selectedDate: Dayjs | null | undefined;
    handleSelectDay: (date: Dayjs) => void;
    availabilities: schedulingV2.self.ISelfSchedulingAvailabilities;
    selectedMonth: number;
    setSelectedMonth: (date: Dayjs) => void;
    isLoadingScheduler?: boolean;
    dateSelectorFormat?: string;
}

const PatientSchedulingCalendar: React.FC<IProps> = memo(
    ({
        selectedDate,
        handleSelectDay,
        availabilities,
        selectedMonth,
        setSelectedMonth,
        isLoadingScheduler,
        dateSelectorFormat,
    }) => {
        const { year, addMonth, subtractMonth } = useCalendarMonthYear(dayjs());

        const [calendar, buildCalendar, isLoadingCalendar] = useCalendar();

        const { minDate, maxDate } = usePatientSelfSchedulingContext();

        useEffect(() => {
            logDev(`Building calendar with month - ${selectedMonth} and year - ${year}`);
            buildCalendar(selectedMonth, year);
        }, [selectedMonth, year, buildCalendar]);

        const today = useMemo(() => zeroTime(dayjs()), []);

        const handleAddMonth = useThrottleFunction(() => {
            setSelectedMonth(addMonth());
        }, 200);

        const handleSubtractMonth = useThrottleFunction(() => {
            const newMonthDayjs = subtractMonth();
            if (newMonthDayjs) {
                setSelectedMonth(newMonthDayjs);
            }
        }, 200);

        const getAvailableDay = useCallback(
            (day: Dayjs): boolean => {
                const formatted = day.format(DATE_TIME_FORMATS.Scheduler.ScheduleDateFormat);
                return Boolean(
                    availabilities.slotsByDate?.[formatted] &&
                        availabilities.slotsByDate?.[formatted].length > 0 &&
                        availabilities.slotsByDate?.[formatted][
                            availabilities.slotsByDate?.[formatted].length - 1
                        ].startUnix > minDate.valueOf() &&
                        availabilities.slotsByDate?.[formatted][0].startUnix < maxDate.valueOf(),
                );
            },
            [availabilities.slotsByDate, minDate, maxDate],
        );

        const handleChangeDate = useCallback(
            (date: Dayjs | null) => {
                if (!date) return;
                selectedDate !== null && handleSelectDay(date);
                setSelectedMonth(date);
            },
            [setSelectedMonth, selectedDate, handleSelectDay],
        );

        const renderDate = useMemo(() => {
            if (selectedDate) {
                return selectedDate;
            } else if (selectedDate === null) {
                return dayjs().month(selectedMonth).year(year);
            } else {
                return undefined;
            }
        }, [selectedDate, selectedMonth, year]);

        /**
         * Check each schedule to see if day is on schedule.
         * If all (practitioner) schedules for the day are full. Return null.
         *
         * @param {Dayjs} day
         * @return {*}  {(Dayjs | null | undefined)}
         */
        const getScheduledDay = useCallback(
            (day: Dayjs): boolean => {
                const formatted = day.format(DATE_TIME_FORMATS.Scheduler.ScheduleDateFormat);
                return availabilities.slotsByDate?.[formatted] &&
                    !day.isAfter(maxDate) &&
                    !day.isBefore(minDate.subtract(1, "day"))
                    ? true
                    : false;
            },
            [availabilities.slotsByDate, minDate, maxDate],
        );

        if (!calendar) {
            return <CenteredLoadingSpinner message={"Loading Calendar..."} />;
        }

        return (
            <div id="calendar" className="cal">
                <div id="calendar-header" className="row align-items-center">
                    <div className="d-flex flex-row mx-auto sticky-top align-items-center">
                        <Button
                            className="px-3 mr-2"
                            variant="light"
                            onClick={handleSubtractMonth}
                            disabled={isLoadingScheduler || isLoadingCalendar}
                        >
                            <FiChevronLeft size={"1.5em"} />
                        </Button>

                        <LobbieDatePickerV2
                            disabled={isLoadingScheduler || isLoadingCalendar}
                            minDate={minDate}
                            format={dateSelectorFormat}
                            maxDate={maxDate}
                            onChange={handleChangeDate}
                            value={renderDate}
                            inputStyle={{
                                borderColor: LOBBIE_COLORS.primary,
                                fontWeight: 500,
                                color: LOBBIE_COLORS.primary,
                            }}
                            outlineStyle={{ borderColor: LOBBIE_COLORS.primary }}
                            iconStyle={{ color: LOBBIE_COLORS.primary }}
                            views={["year", "month"]}
                            openTo="month"
                        />

                        <Button
                            id="cy-add-month"
                            variant="light"
                            className="px-3 ml-2"
                            onClick={handleAddMonth}
                            disabled={isLoadingScheduler || isLoadingCalendar}
                        >
                            <FiChevronRight size={"1.5em"} />
                        </Button>
                    </div>
                </div>
                <hr />
                <table className={"cal-table"}>
                    <thead className={"cal-header"}>
                        <tr>
                            <th>Su</th>
                            <th>Mo</th>
                            <th>Tu</th>
                            <th>We</th>
                            <th>Th</th>
                            <th>Fr</th>
                            <th>Sa</th>
                        </tr>
                    </thead>
                    <tbody>
                        {calendar.map((week: IWeek) => (
                            <CalendarWeek
                                key={`${year}-${selectedMonth}-${week.week}`}
                                today={today}
                                month={selectedMonth}
                                week={week}
                                selectedDate={selectedDate}
                                selectDay={handleSelectDay}
                                getAvailableDay={getAvailableDay}
                                isPatientScheduling={true}
                                getScheduledDay={getScheduledDay}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        );
    },
    (prevProps, nextProps) => {
        const isSameSelectedDate =
            (!prevProps.selectedDate && !nextProps.selectedDate) ||
            Boolean(
                prevProps.selectedDate &&
                    nextProps.selectedDate &&
                    prevProps.selectedDate.isSame(nextProps.selectedDate),
            );
        const isEqual =
            prevProps.selectedMonth === nextProps.selectedMonth &&
            isSameSelectedDate &&
            prevProps.availabilities === nextProps.availabilities;
        logDev("Calendar - memoized?", isEqual);
        return isEqual;
    },
);

export default PatientSchedulingCalendar;
